import { Box, Card, Paper } from '@mui/material';
import axios from 'axios';
import { MemreButton, MemreFlexBox, MemreIcon, MemreText, IconName } from 'components/core';
import * as Color from 'core/colors';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useEffect, useState } from 'react';

// Add relative time support
dayjs.extend(relativeTime);

export const MemberIdDownloadBanner = () => {
  const user = useCurrentUser();
  const [uploads, setUploads] = useState<any[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch uploads with member ID passwords
  const fetchUploads = async () => {
    setIsRefreshing(true);
    try {
      const response = await axios.get(
        `/api/v3/partners/${user.primaryPartner.id}/user_csv_uploads?include_memberid_passwords=true`
      );
      setUploads(response.data.data);
    } catch (error) {
      console.error('Error fetching uploads:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchUploads();
    const interval = setInterval(fetchUploads, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  // Handle download of member ID passwords
  const handleDownload = (uploadId, memberIdPasswords) => {
    const blob = new Blob([memberIdPasswords], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `memberid_passwords_${uploadId}.csv`;
    link.click();
  };

  const uploadsWithPasswords = uploads.filter((upload) => upload.attributes['memberid-passwords']);

  if (!uploadsWithPasswords.length) {
    return null; // No uploads to display
  }

  // Show up to 3 items unless expanded
  const itemsToShow = expanded ? uploadsWithPasswords : uploadsWithPasswords.slice(0, 3);
  const memberIdLabel = user.primaryPartner.partnerSettings.memberIdSlug || 'Member ID';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        backgroundColor: Color.veryLightBlue,
        border: '1px solid #d8e3ec',
      }}
    >
      <MemreFlexBox direction="row" justify="space-between" align="center">
        <Box>
          <MemreText variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
            {memberIdLabel} Passwords Available
          </MemreText>
          <MemreText variant="meta" sx={{ color: '#667085' }}>
            From recent CSV uploads
          </MemreText>
        </Box>
        <MemreButton
          text="Refresh"
          iconName="rotate"
          variant="contained"
          color="secondary"
          onClick={fetchUploads}
          disabled={isRefreshing}
          sx={{ borderRadius: 1 }}
        />
      </MemreFlexBox>

      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {itemsToShow.map((upload) => {
          const { id } = upload;
          const createdAt = upload.attributes['created-at'];
          const memberIdPasswords = upload.attributes['memberid-passwords'];

          return (
            <Card
              key={id}
              elevation={0}
              sx={{
                border: '1px solid #eaecf0',
                borderRadius: 1.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.04)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <MemreFlexBox direction="row" justify="space-between" align="center" sx={{ p: 2 }}>
                <MemreFlexBox direction="row" align="center" gap={2}>
                  <Box sx={{ color: '#4573d2', fontSize: '1.25rem', display: 'flex' }}>
                    <MemreIcon iconName={IconName.document} />
                  </Box>
                  <Box>
                    <MemreText sx={{ fontWeight: 500, fontSize: '0.875rem', mb: 0.5 }}>ID: {id}</MemreText>
                    <MemreText variant="meta" sx={{ color: '#667085' }}>
                      {dayjs(createdAt).fromNow()}
                    </MemreText>
                  </Box>
                </MemreFlexBox>

                <MemreButton
                  text="Download"
                  iconName="download"
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={() => handleDownload(id, memberIdPasswords)}
                  sx={{ borderRadius: 1 }}
                />
              </MemreFlexBox>
            </Card>
          );
        })}
      </Box>

      {uploadsWithPasswords.length > 3 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <MemreButton
            text={expanded ? 'Show Less' : 'Show More'}
            variant="outlined"
            color="secondary"
            onClick={() => setExpanded(!expanded)}
            size="small"
            sx={{ borderRadius: 1 }}
          />
        </Box>
      )}
    </Paper>
  );
};
